import { useMemo, type CSSProperties } from "react";
import {
  CTEAMSCAROUSEL_OVERSCROLL_BEHAVIOR_X_CONTAIN,
  CTEAMSCAROUSEL_TOUCH_ACTION_PAN_X,
} from "./constants";

interface UseCarouselLayoutStylesParams {
  cardWidth: number;
  isMobile: boolean;
  style?: CSSProperties;
  vw: number;
}

interface CarouselLayoutStyles {
  rootStyle: CSSProperties;
  scrollerStyle: CSSProperties;
  spacerStyle: CSSProperties;
}

enum CarouselMaskMode {
  Mobile = "mobile",
  Desktop = "desktop",
}

const SCROLLER_MASK_BY_MODE: Record<CarouselMaskMode, string> = {
  [CarouselMaskMode.Mobile]:
    "linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)",
  [CarouselMaskMode.Desktop]:
    "linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%)",
};

export function useCarouselLayoutStyles({
  cardWidth,
  isMobile,
  style,
  vw,
}: UseCarouselLayoutStylesParams): CarouselLayoutStyles {
  const maskMode = isMobile ? CarouselMaskMode.Mobile : CarouselMaskMode.Desktop;
  const maskImage = SCROLLER_MASK_BY_MODE[maskMode];
  const rootStyle = useMemo<CSSProperties>(() => ({
    display: "flex",
    width: "100%",
    maxWidth: vw * 100,
    padding: "40px 0",
    position: "relative",
    overflow: "hidden",
    ...style,
  }), [style, vw]);
  const scrollerStyle = useMemo<CSSProperties>(() => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: "10px 0",
    width: "100%",
    overflowX: "auto",
    overflowY: "hidden",
    overscrollBehaviorX: CTEAMSCAROUSEL_OVERSCROLL_BEHAVIOR_X_CONTAIN,
    scrollSnapType: "x mandatory",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    touchAction: CTEAMSCAROUSEL_TOUCH_ACTION_PAN_X,
    maskImage,
    WebkitMaskImage: maskImage,
  }), [maskImage]);
  // `spacerStyle` is handed to the memoized <CarouselCardStrip>: a fresh object
  // here re-renders all 255 slots on every carousel render.
  const spacerStyle = useMemo<CSSProperties>(() => ({
    flexShrink: 0,
    width: `calc(50% - ${cardWidth / 2}px)`,
  }), [cardWidth]);

  return useMemo<CarouselLayoutStyles>(() => ({
    rootStyle,
    scrollerStyle,
    spacerStyle,
  }), [rootStyle, scrollerStyle, spacerStyle]);
}
